<template>
    <!--
      Header 导航栏
      - 根据路由自动切换白色/黑色主题（通过 isWhiteTheme / isScrolledTheme 控制）
      - 多层导航菜单统一由一个 el-dropdown + v-for 渲染（根据 item.list 判断单层/双层结构）
    -->
    <div
        class="header-wrapper h-64px px-40 backdrop-blur-[14px] w-full flex items-center justify-between text-#000 fixed left-0 top-0 z-99 bg-transparent"
        :class="{
            'theme-white': isWhiteTheme,
            'theme-scrolled': isScrolledTheme,
            '!b-0': matchPath(route.path, '/CommunityMembers'),
        }"
    >
        <!-- dropdownVisible: dropdownVisible, -->
        <div class="flex-center">
            <img
                :src="logoUrl"
                alt="FlagOS"
                class="w-120px h-23px mt-6 cursor-pointer"
                :class="{ '!h-21': getCurrentLocale === LocaleTypeEnum.English }"
                @click="router.push('/Home')"
            />
            <div class="flex gap-24 ml-30">
                <div class="menu-item cursor-pointer whitespace-nowrap" :class="{ active: getMenuActive('common.projects') }">
                    <MenuProjects
                        @visible-change="handleDropdownVisibleChange"
                        @jump-page="child => handleJumpPage(getMenu('common.projects'), child)"
                    />
                </div>
                <div class="menu-item cursor-pointer whitespace-nowrap" :class="{ active: getMenuActive('common.tools') }">
                    <MenuTools @visible-change="handleDropdownVisibleChange" @jump-page="child => handleJumpPage(getMenu('common.tools'), child)" />
                </div>
                <div class="menu-item cursor-pointer whitespace-nowrap" :class="{ active: getMenuActive('common.developerCenter') }">
                    <MenuDeveloperCenter
                        @visible-change="handleDropdownVisibleChange"
                        @jump-page="child => handleJumpPage(getMenu('common.developerCenter'), child)"
                    />
                </div>
                <div class="menu-item cursor-pointer whitespace-nowrap" :class="{ active: getMenuActive('common.metting') }">
                    <span @click="handleJumpPage(getMenu('common.metting'))">{{ t('common.metting') }}</span>
                </div>
                <div class="menu-item cursor-pointer whitespace-nowrap" :class="{ active: getMenuActive('common.events') }">
                    <span @click="handleJumpPage(getMenu('common.events'))">{{ t('common.events') }}</span>
                </div>
                <div class="menu-item cursor-pointer whitespace-nowrap" :class="{ active: getMenuActive('common.onlineLab') }">
                    <span @click="handleJumpPage(getMenu('common.onlineLab'))">{{ t('common.onlineLab') }}</span>
                </div>
                <div class="menu-item cursor-pointer whitespace-nowrap" :class="{ active: getMenuActive('common.about') }">
                    <MenuAbout @visible-change="handleDropdownVisibleChange" @jump-page="child => handleJumpPage(getMenu('common.about'), child)" />
                </div>
            </div>
        </div>
        <div class="flex-center gap-24" :class="isWhiteTheme ? 'text-#ffffff' : 'text-#262642'">
            <Locale :isWhiteTheme="isWhiteTheme || dropdownVisible" />

            <el-popover
                v-model:visible="messagePopoverVisible"
                trigger="click"
                placement="bottom-end"
                :popper-options="{
                    modifiers: [
                        {
                            name: 'offset',
                            options: {
                                offset: [124, 16],
                            },
                        },
                    ],
                }"
                popper-class="message-popover"
                @before-enter="beforeMessagePopoverEnter"
                @hide="handleMessagePopoverHide"
            >
                <template #reference>
                    <div class="relative flex-center">
                        <icon-svg-message class="text-22 cursor-pointer w-22 h-22" :size="22" :color="isWhiteTheme ? '#ffffff' : '#262642'" />
                        <span
                            v-if="unreadCount > 0"
                            class="right_count absolute top--4 right--4 w-12 h-12 bg-#e80000 rounded-50% flex-center text-7px text-#ffffff"
                        >
                            {{ unreadCount > 99 ? '99+' : unreadCount }}
                        </span>
                    </div>
                </template>

                <MessageList @close="messagePopoverVisible = false" />
            </el-popover>
            <Login v-if="!userName" />
            <Avatar v-if="userName" class="header_avatar" :class="isWhiteTheme || dropdownVisible ? 'text-#ffffff' : 'text-#262642'" />
        </div>
    </div>
</template>
<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import { openLink } from '@/utils/global';
import Avatar from './Avatar.vue';
import Login from './Login.vue';
import Locale from './Locale.vue';
import MessageList from './MessageList.vue';
import MenuProjects from './menu/MenuProjects.vue';
import MenuTools from './menu/MenuTools.vue';
import MenuDeveloperCenter from './menu/MenuDeveloperCenter.vue';
import MenuAbout from './menu/MenuAbout.vue';
import { LocaleTypeEnum } from '@/store/locale';
import { ElMessage } from 'element-plus';
import logoBlackEn from '@/assets/svgs/logo-black-en.svg';
import logoBlack from '@/assets/svgs/logo-black.svg';
import logoEn from '@/assets/svgs/logo-en.svg';
import logo from '@/assets/svgs/logo.svg';
import throttle from 'lodash/throttle';

import { getUnreadCountApi, getMessageListApi } from '@/apis/message';
import { menuList as rawMenuList, type MenuItem, type DropdownItem } from '@/layout/menuData';

const { userName } = useStore('user');
const { getCurrentLocale } = useStore('locale');
const { t } = useI18n();
const router = useRouter();
const route = useRoute();
const messagePopoverVisible = ref(false);
const { dropdownVisible, setDropdownVisible, unreadCount, updateHeaderMessageCenter, realtimeStream, setRealtimeStream, headerWhiteTheme } =
    useStore('common');

const isWhiteTheme = ref(false);
const isScrolledTheme = ref(false);

watch(headerWhiteTheme, val => {
    if (val !== null) {
        isWhiteTheme.value = val;
    }
});

/** 大小写不敏感的路径前缀匹配 */
const matchPath = (path: string, targetPath: string): boolean => {
    if (!path || !targetPath) return false;
    return path.toLowerCase().indexOf(targetPath.toLowerCase()) >= 0;
};

/** 匹配多个目标路径 */
const matchAnyPath = (path: string, targetPaths: string[]): boolean => {
    return targetPaths.some(targetPath => matchPath(path, targetPath));
};

// 各页面 banner 区域高度阈值，决定 header 何时从透明切换为实色背景
const SCROLL_THRESHOLDS = {
    home: {
        // Home 的 banner 是 100vh，减去 header 高度
        getThreshold: () => window.innerHeight - 80,
    },
    events: {
        // 赛事列表页 banner 图固定 390px
        getThreshold: () => 390 - 80,
    },
    onlineLab: {
        // 实验室页 banner 图固定 180px
        getThreshold: () => 180 - 80,
    },
};

// 需要监听滚动来切换 header 主题的页面
const SCROLL_PAGES: string[] = [
    '/events',
    '/EventDetail',
    '/RaceDetail',
    '/race-detail-season2',
    '/OnlineLab',
    '/Register',
    '/my-event',
    '/About',
    '/Conferences',
];

// 根据主题 + 语言切换 logo 图片
// 深色背景（白色主题或下拉展开时）→ 浅色 logo；否则 → 深色 logo
// 用 watch 替代 computed 以绕过 pinia-auto-refs 的嵌套 computed 跟踪问题
const logoUrl = ref(logo);

watch(
    [isWhiteTheme, dropdownVisible, getCurrentLocale],
    ([newIsWhite, newDropdownVisible, newLocale]) => {
        const isDark = !(newIsWhite || newDropdownVisible);
        console.log('isDark', isDark);
        if (isDark) {
            logoUrl.value = newLocale === LocaleTypeEnum.English ? logoBlackEn : logoBlack;
        } else {
            logoUrl.value = newLocale === LocaleTypeEnum.English ? logoEn : logo;
        }
    },
    { immediate: true }
);
const getMenu = (name: string): MenuItem => {
    return state.menuList.find(m => m.name === name)!;
};

const getMenuActive = (name: string): boolean => {
    return state.menuList.find(m => m.name === name)?.active || false;
};

const getUnreadCount = async () => {
    try {
        const res = await getUnreadCountApi();
        updateHeaderMessageCenter({ unreadCount: res?.data?.unread_count || 0 });
        setRealtimeStream('');
    } catch (error) {
        updateHeaderMessageCenter({ unreadCount: 0 });
    }
};

watch(
    () => realtimeStream.value,
    async newVal => {
        if (newVal === 'site_message.new') {
            await getUnreadCount();
        }
    }
);

const getMessageList = async () => {
    try {
        const res = await getMessageListApi({
            page: 1,
            page_size: 5,
            category: 'system',
        });
        updateHeaderMessageCenter({ messageList: res?.data?.list || [] });
    } catch (error) {
        console.log(error);
    }
};

const handleDropdownVisibleChange = (visible: boolean) => {
    setDropdownVisible(visible);
    document.body.style.overflow = visible ? 'hidden' : '';
};

const handleMessagePopoverHide = () => {
    updateHeaderMessageCenter({ messageList: [] });
};

const beforeMessagePopoverEnter = () => {
    Promise.all([getUnreadCount(), getMessageList()]);
};

// 用 reactive 包裹导入的菜单数据，active 状态的变更才能触发响应式更新
const state = reactive({
    menuList: rawMenuList.map(item => ({ ...item })),
});

/**
 * 根据当前路由确定哪个一级菜单高亮
 * 第一轮：精确匹配一级菜单的 path（tab 优先）
 * 第二轮：兜底匹配 dropdown 子项的 path
 */
const getActiveMenuIndex = (menuList: MenuItem[], currentPath: string) => {
    // 第一轮：匹配一级菜单路径
    let index = menuList.findIndex((item: MenuItem) => {
        if (!item.path) return false;

        // EventDetail / RaceDetail 归为 events 高亮
        if (matchPath(item.path, '/events') && matchAnyPath(currentPath, ['/EventDetail', '/RaceDetail', '/race-detail-season2'])) {
            return true;
        }

        if (matchPath(item.path, '/onlineLab') && matchPath(currentPath, '/OnlineLab')) {
            return true;
        }

        return matchPath(currentPath, item.path);
    });

    if (index !== -1) return index;

    // 第二轮：匹配 dropdown 子项路径（兜底）
    index = menuList.findIndex((item: MenuItem) => {
        if (!item.dropdownList) return false;

        return item.dropdownList.some((childItem: DropdownItem) => {
            if (!childItem.path) return false;
            return matchPath(currentPath, childItem.path);
        });
    });

    return index;
};

/**
 * 根据当前路由初始化 header 主题 & 滚动监听
 * - Home：独立 handler，阈值 = 视口高度
 * - events / OnlineLab 等：共用 handler，阈值 = banner 图高度
 * - 其他页面：固定深色主题，不监听滚动
 */
function initPageTheme() {
    window.removeEventListener('scroll', handleScroll);
    window.removeEventListener('scroll', handleEventsScroll);
    window.removeEventListener('scroll', handleMeetingScroll);

    if (matchPath(route.path, '/Home')) {
        isWhiteTheme.value = false;
        isScrolledTheme.value = false;
        window.addEventListener('scroll', handleScroll);
    } else if (matchAnyPath(route.path, SCROLL_PAGES)) {
        console.log(9999);
        isWhiteTheme.value = true;
        isScrolledTheme.value = false;
        window.addEventListener('scroll', handleEventsScroll);
        nextTick(() => {
            handleEventsScroll();
        });
    } else if (matchPath(route.path, '/Conferences') || matchPath(route.path, '/MeetingDetail')) {
        isScrolledTheme.value = false;
        window.addEventListener('scroll', handleMeetingScroll);
    } else {
        isWhiteTheme.value = false;
        isScrolledTheme.value = false;
    }
}

// 路由变化时：更新菜单高亮状态 + 重新初始化主题/滚动
// immediate: true 确保首次进入页面也能正确初始化

watch(
    () => route.path,
    () => {
        nextTick(() => {
            console.log(state.menuList, route.path)
            state.menuList.forEach((item: MenuItem) => {
                item.active = false;
            });
            const curMenuIdx = getActiveMenuIndex(state.menuList, route.path);
            if (curMenuIdx >= 0) {
                state.menuList[curMenuIdx].active = true;
            }
            initPageTheme();
        });
    },
    { immediate: true }
);
//
const swipeIndex = ref(0);
onMounted(async () => {
    await getUnreadCount();
    window.addEventListener('banner-slide-change', handleBannerSlideChange);
});

/**
 * 处理导航菜单点击
 * - childMenu 存在时：根据 type 分发（route → router.push / link → openLink / unopen → 提示）
 * - 其他一级菜单：更新 active 高亮后执行 router.push
 */
const dropdownRefs = reactive<Record<number, any>>({});
const setDropdownRef = (index: number, el: any) => {
    if (el) dropdownRefs[index] = el;
};
function handleJumpPage(menu: MenuItem, childMenu?: DropdownItem, menuIndex?: number) {
    // 下拉子项：按类型分发
    if (childMenu) {
        if (childMenu.type === 'route') {
            router.push(childMenu.path!);
            if (menuIndex !== undefined) dropdownRefs[menuIndex]?.handleClose();
            return;
        }
        if (childMenu.type === 'unopen') {
            ElMessage.warning(t('projects.unopentip'));
            return;
        }
        if (childMenu.type === 'link') {
            // 英文状态下优先使用独立英文链接
            if (getCurrentLocale.value === LocaleTypeEnum.English) {
                if (childMenu.enPath) {
                    openLink(childMenu.enPath);
                    return;
                }
                if (childMenu.isEnSeparateLink) {
                    openLink(`${childMenu.path}?lang=en`);
                    return;
                }
            }
            openLink(childMenu.path!);
            return;
        }
    }

    // 普通一级菜单：更新高亮后路由跳转
    state.menuList.forEach((item: MenuItem) => {
        item.active = false;
    });
    menu.active = true;
    const targetPath = childMenu?.path || menu.path;
    if (targetPath) {
        router.push(targetPath);
    }
}

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore - lodash/throttle 在本项目中缺少声明文件，这里仅为消除 TS 报错

// Home 页面的滚动处理：背景透明 → 实色（阈值 = 首屏高度）
const handleScroll = throttle(function () {
    const threshold = SCROLL_THRESHOLDS.home.getThreshold();
    updateThemeByScroll(threshold);
    if (matchPath(route.path, '/Home')) {
        // updateThemeByActiveIndex(0);
    }
}, 200);

// 赛事 / 实验室页面的滚动处理（阈值 = 各自 banner 高度）
const handleEventsScroll = throttle(function () {
    const threshold = matchPath(route.path, '/OnlineLab') ? SCROLL_THRESHOLDS.onlineLab.getThreshold() : SCROLL_THRESHOLDS.events.getThreshold();
    updateThemeByScroll(threshold);
}, 200);

// Meeting 页面的滚动处理：超过一屏时切换为实色背景
const handleMeetingScroll = throttle(() => {
    const scrollTop = window.scrollY || document.documentElement.scrollTop || 0;
    isScrolledTheme.value = scrollTop > window.innerHeight - 80;
}, 200);

// 根据滚动位置切换 header 主题
// scrollTop > threshold → 实色背景（黑色 theme-scrolled）
// scrollTop <= threshold → 透明背景（白色 theme-white）
const updateThemeByScroll = (threshold: number) => {
    const scrollTop = window.scrollY || document.documentElement.scrollTop || 0;
    if (scrollTop > threshold || (swipeIndex.value === 0 && matchPath(route.path, '/Home'))) {
        isWhiteTheme.value = false;
        isScrolledTheme.value = true;
    } else {
        isWhiteTheme.value = true;
        isScrolledTheme.value = false;
    }
};

const handleBannerSlideChange = (e: Event) => {
    if (!matchPath(route.path, '/Home')) return;
    swipeIndex.value = (e as CustomEvent).detail?.activeIndex ?? 0;
    const threshold = SCROLL_THRESHOLDS.home.getThreshold();
    updateThemeByScroll(threshold);
};

onUnmounted(() => {
    window.removeEventListener('scroll', handleScroll);
    window.removeEventListener('scroll', handleEventsScroll);
    window.removeEventListener('scroll', handleMeetingScroll);
    window.removeEventListener('banner-slide-change', handleBannerSlideChange);
});
</script>
<style scoped lang="scss">
.header-wrapper {
    transition: all 0.3s ease;
    border-bottom: 1px solid rgba(0, 0, 0, 0.13);

    &.theme-white {
        color: #fff;
        border: none;
        background: transparent;

        svg {
            color: #fff;
        }

        .menu-item {
            &::before {
                background-color: #fff;
            }

            :deep(.el-dropdown) {
                color: #fff;
            }
        }
    }

    &.theme-scrolled {
        background: #fff;
        color: #262642;
        border-bottom: 1px solid rgba(0, 0, 0, 0.13);

        svg {
            color: #262642;
        }

        .menu-item {
            &::before {
                background-color: #262642;
            }

            :deep(.el-dropdown) {
                color: #262642;
            }
        }
    }

    &.dropdownVisible {
        background: #07111e !important;
        opacity: 0.95 !important;
        color: #fff;
        border: none;

        svg {
            color: #fff;
        }

        .menu-item {
            &::before {
                background-color: #fff;
            }

            :deep(.el-dropdown) {
                color: #fff;
            }
        }
    }

    svg {
        color: #262642;
    }

    .menu-item {
        display: flex;
        align-items: center;
        position: relative;
        font-size: 16px;

        &::before {
            content: '';
            display: block;
            position: absolute;
            left: 0;
            bottom: -19px;
            width: 0;
            height: 2px;
            border-radius: 1px;
            background-color: #262642;
            transition: all 0.3s ease;
            transform-origin: center;
        }

        &.active {
            font-weight: 600;

            &::before {
                width: 100%;
            }
        }

        :deep(.el-dropdown) {
            color: #262642;
        }
    }
}
</style>

<style lang="scss">
.message-popover {
    border-radius: 8px !important;
    width: 400px !important;
    max-width: 400px !important;
    z-index: 2026 !important;
    padding: 0 !important;
}

.custom-dropdown-new {
    .el-dropdown-menu {
        flex-wrap: nowrap !important;
        overflow-y: auto;
    }

    .el-dropdown-menu__item {
        min-height: auto !important;
    }
}
</style>
